import type { AnyID } from '@types'
import { parseRelaxArmVariantCatalog } from '@utils/atemporal/relaxArmVariants'
import { pickLocalizedText } from '@utils/i18n/uiLocale'

const isArmCatalog = (catalogID: unknown) => /^BR\d+(-\d+)?$/i.test(String(catalogID || '').trim())

/**
 * Unidades RELAX (su brazo se gestiona por cambio de GLB, no por brazos BR
 * independientes). Sus catálogos pueden terminar en «D»/«I» (p. ej. «167 BR3 D»
 * o «167-95-95i»), pero NO son series de un solo brazo «con mano»: deben quedar
 * fuera de esa clasificación para no disparar la migración/purga de brazos ni el
 * aviso de «un brazo de un lado» al reescribir su variante.
 */
const isRelaxManagedCatalog = (catalogID: unknown) => {
  // Las variantes RELAX integradas contienen el token «BRn» (p. ej. «167 BR3 D»
  // o «167-105 BR3 D»); las series «con mano» reales (asientos tipo «150-95 D»)
  // nunca lo llevan. Esta detección es agnóstica al código base y al sufijo de
  // fondo, a diferencia de parsear el patrón completo de la variante.
  if (/\bbr\d+\b/i.test(String(catalogID || ''))) return true
  return parseRelaxArmVariantCatalog(catalogID) != null
}

const normalizeCatalogCode = (catalogID: unknown) =>
  String(catalogID || '')
    .trim()
    .toLowerCase()

const SINGLE_ARM_SERIES_PREFIXES = [
  '100',
  '105',
  '110',
  '115',
  '120',
  '125',
  '130',
  '135',
  '140',
  '145',
  '150',
  '155',
] as const

export type LinkedArmMigrationSnapshot = {
  oldArmId: string
  oldCatalogID: string
  preservedSideSign: 1 | -1
  depth: number
}

/** Series de un brazo: conservar un brazo (lado preferido) y eliminar el resto. */
export function pickArmSnapshotToKeepForSingleArmSeries(
  arms: LinkedArmMigrationSnapshot[],
  preferredSide?: 1 | -1 | null,
): { keep: LinkedArmMigrationSnapshot | null; remove: LinkedArmMigrationSnapshot[] } {
  if (!arms.length) return { keep: null, remove: [] }
  if (arms.length === 1) return { keep: arms[0], remove: [] }

  const side: 1 | -1 =
    preferredSide === 1 || preferredSide === -1 ? preferredSide : arms[0].preservedSideSign
  const onPreferredSide = arms.filter((arm) => arm.preservedSideSign === side)
  const keep = onPreferredSide[0] || arms[0]
  return {
    keep,
    remove: arms.filter((arm) => arm.oldArmId !== keep.oldArmId),
  }
}

/** Series de un brazo (90, 100..155 en pasos de 5) → como máximo un brazo. */
export const isSingleArmSeries = (catalogID: unknown) => {
  const code = normalizeCatalogCode(catalogID)
  return SINGLE_ARM_SERIES_PREFIXES.some(
    (prefix) => code === prefix || code.startsWith(`${prefix}-`),
  )
}

/**
 * Letra de mano al final del código («181-100 D» → d, «185-95 I» → i).
 * Solo letra suelta tras espacio: no confundir con sufijos de acabado (Li, La…)
 * ni con rinconeros tipo 166-95-95i.
 *
 * Corner XL Oreja (177–180): «177-95 D» / «177-95i» — misma letra D/I, pero el
 * labio del BR es el contrario al de Corner XL Brazo / series 100–155.
 */
export const unitArmHandednessLetter = (catalogID: unknown): 'd' | 'i' | null => {
  // Las unidades RELAX no son series «con mano»: su brazo es por cambio de GLB.
  if (isRelaxManagedCatalog(catalogID)) return null
  const code = normalizeCatalogCode(catalogID)

  const spaced = code.match(/\s(d|i)$/)
  if (spaced) return spaced[1] as 'd' | 'i'

  // Oreja sin espacio (177-95i). Evita 166-…i y 181–185.
  if (/^(177|178|179|180)([\d.-]*)[id]$/i.test(code)) {
    return code.endsWith('i') ? 'i' : 'd'
  }

  return null
}

/** Corner XL Oreja (177–180), p. ej. «177-95 D» o «177-95i». */
export const isCornerXlOrejaCatalog = (catalogID: unknown) => {
  const code = normalizeCatalogCode(catalogID)
  return /^(177|178|179|180)(-|\d|\s|$)/i.test(code)
}

/**
 * Lado de brazo permitido según la mano.
 * Corner XL Brazo / series 100–155 (verificado en escena):
 *   «… D» → +1 (derecha local), «… I» → -1 (izquierda local).
 * Corner XL Oreja 177–180 («177-95 D»): labio INVERSO —
 *   D → -1, I → +1 (BR en parent+armW; D→+1 lo dejaba en parent−width).
 * null = sin restricción de mano.
 */
export const allowedArmSideForUnit = (catalogID: unknown): 1 | -1 | null => {
  const letter = unitArmHandednessLetter(catalogID)
  if (letter == null) return null

  if (isCornerXlOrejaCatalog(catalogID)) {
    if (letter === 'd') return -1
    if (letter === 'i') return 1
    return null
  }

  if (letter === 'd') return 1
  if (letter === 'i') return -1
  return null
}

export function collectArmIdsLinkedToParent(
  items: Map<AnyID, any>,
  parentId: string,
  parentSnapshot?: any,
): AnyID[] {
  const ids = new Set<AnyID>()
  const pid = String(parentId)
  items.forEach((sceneItem, sceneId) => {
    if (!sceneItem || !isArmCatalog(sceneItem.catalogID)) return
    if (String(sceneItem.firstParentID || '') === pid) ids.add(sceneId as AnyID)
  })
  const parent = parentSnapshot ?? items.get(parentId as AnyID)
  const childs = parent?.childs
  if (childs) {
    for (const childId of childs as Set<string>) {
      const child = items.get(childId as AnyID)
      if (child && isArmCatalog(child.catalogID)) ids.add(childId as AnyID)
    }
  }
  return Array.from(ids)
}

export const unitAllowsOnlyOneArm = (unit: { catalogID?: unknown }) =>
  isSingleArmSeries(unit?.catalogID) || unitArmHandednessLetter(unit?.catalogID) != null

export const ARM_NOTICE_ONLY_ONE_SIDE = () =>
  pickLocalizedText({
    es: 'Esta unidad solo permite un brazo de un lado.',
    en: 'This unit only allows an arm on one side.',
  })

export const ARM_NOTICE_HANDED_SIDE = () =>
  pickLocalizedText({
    es: 'Esta unidad solo admite el brazo en el lado contrario a su mano.',
    en: 'This unit only allows the arm on the side opposite its handedness.',
  })

export const ARM_NOTICE_GROUP_FULL = () =>
  pickLocalizedText({
    es: 'Estas unidades ya tienen su brazo cada una y no permiten más.',
    en: 'These units already have one arm each and do not allow more.',
  })

export const ARM_NOTICE_NO_ARMS = () =>
  pickLocalizedText({
    es: 'Estas unidades no permiten ningún brazo.',
    en: 'These units do not allow any arms.',
  })

export const ARM_NOTICE_REPLACE_WITHOUT_ARMS = () =>
  pickLocalizedText({
    es: 'Está sustituyendo por una unidad sin brazos.',
    en: 'You are replacing with an armless unit.',
  })
